import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, GraduationCapIcon, BriefcaseIcon, CarIcon, HeartPulseIcon, TrendingUpIcon, HomeIcon, StethoscopeIcon, SmartphoneIcon, ArrowRightIcon, StarIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface ProductCatalogueProps {
  navigate: (screen: string) => void;
}
export function ProductCatalogue({
  navigate
}: ProductCatalogueProps) {
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'Savings', 'Protection', 'Health', 'Assets'];
  const products = [{
    id: 'job-loss',
    title: 'Job Loss Cover',
    desc: 'Income protection if you lose your job.',
    price: '₦2,500',
    icon: BriefcaseIcon,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    category: 'Protection'
  }, {
    id: 'motor',
    title: 'Motor Vehicle',
    desc: 'Comprehensive cover + savings.',
    price: '₦15,000',
    icon: CarIcon,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    category: 'Assets'
  }, {
    id: 'life',
    title: 'Life & Disability',
    desc: 'Pure protection + long-term savings.',
    price: '₦3,000',
    icon: HeartPulseIcon,
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    category: 'Protection'
  }, {
    id: 'annuity',
    title: 'General Annuity',
    desc: 'Guaranteed income for life.',
    price: '₦10,000',
    icon: TrendingUpIcon,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    category: 'Savings'
  }, {
    id: 'home',
    title: 'Home Insurance',
    desc: 'Protect property & save for upgrades.',
    price: '₦4,000',
    icon: HomeIcon,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    category: 'Assets'
  }, {
    id: 'health',
    title: 'Health Insurance',
    desc: 'HMO and indemnity plans.',
    price: '₦8,000',
    icon: StethoscopeIcon,
    color: 'text-cyan-600',
    bg: 'bg-cyan-50',
    category: 'Health'
  }, {
    id: 'device',
    title: 'Device Insurance',
    desc: 'Protect gadgets against damage.',
    price: '₦500',
    icon: SmartphoneIcon,
    color: 'text-slate-700',
    bg: 'bg-slate-100',
    category: 'Assets'
  }];
  const filteredProducts = activeCategory === 'All' ? products : products.filter(p => p.category === activeCategory);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      y: 10
    },
    show: {
      opacity: 1,
      scale: 1,
      y: 0
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 overflow-y-auto pb-24 bg-slate-50" data-id="element-719">
      <TopBar title="Products" onNotificationClick={() => navigate('notifications')} data-id="element-720" />

      <div className="px-5 pt-4 pb-24" data-id="element-721">
        {/* Header & Search */}
        <div className="mb-6" data-id="element-722">
          <h1 className="text-2xl font-bold text-[#1B3B36] mb-4" data-id="element-723">
            Find Protection
          </h1>
          <div className="relative" data-id="element-724">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-725" />
            <input type="text" placeholder="Search products, underwriters..." className="w-full bg-white border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#1B3B36] shadow-sm" data-id="element-726" />
          </div>
        </div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-8 pb-1" data-id="element-727">
          {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-5 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all ${activeCategory === cat ? 'bg-[#1B3B36] text-white shadow-md' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`} data-id="element-728">
              {cat}
            </button>)}
        </div>

        {/* Featured Banner */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-3xl p-6 text-[#1B3B36] mb-8 shadow-lg shadow-amber-500/20 relative overflow-hidden cursor-pointer active:scale-[0.98] transition-transform" data-id="element-729">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-730"></div>
          <div className="relative z-10" data-id="element-731">
            <div className="inline-flex items-center gap-1 bg-white/30 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3" data-id="element-732">
              <StarIcon className="w-3 h-3 fill-[#1B3B36]" data-id="element-733" /> Featured
            </div>
            <h2 className="text-xl font-bold mb-1" data-id="element-734">Child Education Plan</h2>
            <p className="text-sm font-medium opacity-90 mb-4" data-id="element-735">
              Secure their future today.
            </p>
            <button onClick={() => navigate('product-detail')} className="bg-[#1B3B36] text-white text-xs font-bold px-4 py-2 rounded-lg flex items-center gap-1 hover:bg-gray-900 transition-colors" data-id="element-736">
              Explore <ArrowRightIcon className="w-3 h-3" data-id="element-737" />
            </button>
          </div>
        </div>

        {/* Product Grid */}
        <div className="mb-4" data-id="element-738">
          <h2 className="text-lg font-bold text-[#1B3B36] mb-4 px-1" data-id="element-739">
            {activeCategory === 'All' ? 'All Products' : activeCategory}
          </h2>
          <div className="grid grid-cols-2 gap-4" data-id="element-740">
            {products.filter(p => activeCategory === 'All' || p.category === activeCategory).map((product, idx) => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.05
          }} onClick={() => navigate('product-detail')} className="bg-white rounded-3xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all cursor-pointer group" data-id="element-741">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${product.bg} ${product.color} group-hover:scale-110 transition-transform`} data-id="element-742">
                    <product.icon className="w-6 h-6" data-id="element-743" />
                  </div>
                  <h3 className="font-bold text-[#1B3B36] text-sm mb-1 line-clamp-1" data-id="element-744">
                    {product.title}
                  </h3>
                  <p className="text-[11px] text-gray-500 mb-3 line-clamp-2 leading-relaxed" data-id="element-745">
                    {product.desc}
                  </p>
                  <div className="mt-auto pt-3 border-t border-gray-50 flex items-center justify-between" data-id="element-746">
                    <span className="text-xs font-bold text-[#1B3B36]" data-id="element-747">
                      {product.price}
                    </span>
                    <div className="w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-[#1B3B36] group-hover:text-white transition-colors" data-id="element-748">
                      <ArrowRightIcon className="w-3 h-3" data-id="element-749" />
                    </div>
                  </div>
                </motion.div>)}
          </div>
        </div>
      </div>
    </motion.div>;
}